import { Injectable } from "@angular/core";
import { UserAgentApplication, Configuration, AuthenticationParameters, AuthResponse, Logger, LogLevel } from "msal";
import { User } from "../models/user";
import { from, Observable, of } from "rxjs";
import { concatMap, tap, catchError, map } from "rxjs/operators";
import { LoggingService } from "./logging.service";
import { AppConfigService } from "./app-config.service";

@Injectable({
  providedIn: "root",
})
export class AuthService {
  private app: UserAgentApplication;

  constructor(private appConfigService: AppConfigService) {
    this.app = new UserAgentApplication(this.getAppConfig());
  }

  loginSilent$(): Observable<User> {
    LoggingService.log("AuthService - loginSilent");
    return this.acquireTokenSilent$().pipe(
      map(auth => {
        LoggingService.log(`AuthService - authorized - map - ${JSON.stringify(auth.tokenType)}`);
        return this.getUser();
      }),
      catchError(err => {
        LoggingService.log(`AuthService - authorized - err - ${JSON.stringify(err)}`);
        return of(null);
      }),
    );
  }

  loginPopup$(): Observable<User> {
    LoggingService.log("AuthService - loginPopup");
    return from(this.app.loginPopup(this.getAuthParams())).pipe(
      concatMap(auth => {
        LoggingService.log(`AuthService - loginPopup - ${JSON.stringify(auth.tokenType)}`);

        return this.acquireTokenPopup$().pipe(
          map(auth => {
            LoggingService.log(`AuthService - loginPopup from acquireTokenPopup - ${JSON.stringify(auth.tokenType)}`);
            return this.getUser();
          }),
        );
      }),
      catchError(err => {
        LoggingService.log(`AuthService - loginPopup error - ${JSON.stringify(err)}`);

        return of(null);
      }),
    );
  }

  logout() {
    this.app.logout();
  }

  acquireTokenSilent$(): Observable<AuthResponse> {
    LoggingService.log("AuthService - acquireTokenSilent");
    return from(this.app.acquireTokenSilent(this.getAuthParams())).pipe(
      tap(auth => {
        LoggingService.log(`AuthService - acquireTokenSilent - tap - ${JSON.stringify(auth.tokenType)}`);
      }),
      catchError(err => {
        LoggingService.log(`AuthService - acquireTokenSilent - error - ${JSON.stringify(err)}`);

        throw err;
      }),
    );
  }

  private acquireTokenPopup$(): Observable<AuthResponse> {
    LoggingService.log("AuthService - acquireTokenPopup");
    return from(this.app.acquireTokenPopup(this.getAuthParams())).pipe(
      tap(auth => {
        LoggingService.log(`AuthService - acquireTokenPopup - tap - ${JSON.stringify(auth.tokenType)}`);
      }),
      catchError(err => {
        LoggingService.log(`AuthService - acquireTokenPopup - error - ${JSON.stringify(err)}`);
        throw err;
      }),
    );
  }

  private getAuthParams(): AuthenticationParameters {
    return {
      scopes: [`${this.appConfigService.getConfig().adClientId}/user_impersonation`],
    };
  }

  private getAppConfig(): Configuration {
    return {
      auth: {
        clientId: this.appConfigService.getConfig().adClientId,
        authority: "https://login.microsoftonline.com/f0e33944-176f-4680-9c9b-b50ee8139e33",
        redirectUri: `${window.location.origin}/auth.html`,
        postLogoutRedirectUri: `${window.location.origin}/`,
      },
      cache: {
        cacheLocation: "localStorage",
      },
      system: {
        logger: new Logger(LoggingService.msalLogFn, { level: LogLevel.Verbose }),
      },
    };
  }

  private getUser(): User {
    try {
      let account = this.app.getAccount();
      LoggingService.log(`AuthService - got account? - ${JSON.stringify(account)}`);

      if (account) {
        return new User(account.userName);
      }

      return null;
    } catch (error) {
      LoggingService.log(`AuthService - failed to getUser - ${JSON.stringify(error)}`);
      return null;
    }
  }
}
